import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {TexNoiseSockets} from "#material/nodes/texture/tex-noise/definition"
import {texNoiseDefinition} from "#material/nodes/texture/tex-noise/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {ThreeReturns} from "#material/sockets/three"
import type {NoiseTextureNode} from "#material/cycles/types"
import {cyclesVec3} from "#material/cycles/helpers"
import {ThreeTsl as TSL} from "#material/three/exports"
import {threeNoise2d} from "#material/nodes/texture/tex-noise/constants"
import {cyclesDimensions, cyclesType} from "#material/nodes/texture/tex-noise/helpers"

@registerMaterialGraphNode
export class TexNoise extends MaterialNode<TexNoiseSockets> {
    public override get definition() {
        return texNoiseDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TexNoiseSockets>> => {
        const {logger} = evaluator.context
        const scaleValue =
            (await evaluator.get(this.parameters.inputs.scale))?.toFloat() ?? threeConvert(this.parameters.settings.scale, threeValue) ?? threeValue(100)

        logger.warn("WARNING: Noise node not fully implemented!")
        const fac = threeNoise2d(TSL.uv(), TSL.div(scaleValue, threeValue(100)), threeValue(0))

        return {fac, color: TSL.color(fac)}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TexNoiseSockets>> => {
        const noiseTextureNode: NoiseTextureNode = {
            type: "noise_texture",
            inputs: await evaluator.getAll({
                detail: this.parameters.inputs.detail,
                distortion: this.parameters.inputs.distortion,
                gain: this.parameters.inputs.gain,
                lacunarity: this.parameters.inputs.lacunarity,
                offset: this.parameters.inputs.offset,
                roughness: this.parameters.inputs.roughness,
                scale: this.parameters.inputs.scale,
                vector: this.parameters.inputs.vector,
                w: this.parameters.inputs.w,
            }),
            parameters: {
                detail: this.parameters.settings.detail,
                dimensions: cyclesDimensions(this.parameters.settings.noiseDimensions),
                distortion: this.parameters.settings.distortion,
                gain: this.parameters.settings.gain,
                lacunarity: this.parameters.settings.lacunarity,
                offset: this.parameters.settings.offset,
                roughness: this.parameters.settings.roughness,
                scale: this.parameters.settings.scale,
                type: cyclesType(this.parameters.settings.noiseType),
                use_normalize: this.parameters.settings.normalize,
                vector: cyclesVec3(this.parameters.settings.vector),
                w: this.parameters.settings.w,
            },
            resources: {},
        }

        return {color: [noiseTextureNode, "color"], fac: [noiseTextureNode, "fac"]}
    }
}
